import {
	AssignStatus,
	AssignStatusFilter,
	InclusionFilterStatus,
	PriorityFilterStatus,
	SignalFilterStatus,
} from "@/enum/list.enum";

import { SortParams } from "@/types/types";

import { GetUnassignedListRequestPaginationParams } from "../../../../List/schema/list-request-params";
import { IFilterWithValues } from "../../../../List/components/FilterModal";
import { CustomListsAutocompleteForSequencerItem } from "../../../../List/components/CustomListsAutocompleteForSequencer";
import {
	LIST_SCOPE_RELEVANCE_STATUSES,
	ListScope,
} from "../../../components/ListScopeFilter/listScope";

/**
 * The assignStatus population Review-to-send draws from, whatever the status filter says — read
 * from `listScope.ts`, the one module that owns "which statuses live on which surface". Note what
 * this means for a deep link: `assignStatus=["ALL"]` expands to *these three*, so a lead the
 * campaign already owns can never be shown here however wide the link's filters look (PREC-4103).
 */
export const READY_TO_SEND_ASSIGN_STATUSES = LIST_SCOPE_RELEVANCE_STATUSES[ListScope.READY_TO_SEND];

const expandAssignStatus = (selected: AssignStatusFilter[]): AssignStatus[] =>
	selected.reduce<AssignStatus[]>(
		(acc, status) =>
			status === AssignStatusFilter.ALL
				? [...acc, ...READY_TO_SEND_ASSIGN_STATUSES]
				: [...acc, status as unknown as AssignStatus],
		[]
	);

interface ReadyToSendRequestParamsArgs {
	page: number;
	limit: number;
	query: string;
	sorting: SortParams | undefined;
	selectedAssignStatus: AssignStatusFilter[];
	filter: IFilterWithValues | undefined;
	priorityFilterStatus: PriorityFilterStatus[];
	signalFilterStatus: SignalFilterStatus[];
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	/** A frozen select-all selection, resolved server-side (PREC-3648). */
	reviewSessionId: string | null | undefined;
}

/**
 * The `getAllProspects` request Review-to-send fires.
 *
 * PREC-2523: a search runs over the whole surface rather than inside the current view — the
 * status/priority/signal/list narrowing is dropped for the duration of the query, matching what
 * the Outbox already does (`useOutboxProspects`). A review session is not narrowing the user
 * typed, it is the selection they are working through, so a search inside one stays inside it.
 */
export const buildReadyToSendRequestParams = ({
	page,
	limit,
	query,
	sorting,
	selectedAssignStatus,
	filter,
	priorityFilterStatus,
	signalFilterStatus,
	selectedLists,
	reviewSessionId,
}: ReadyToSendRequestParamsArgs): GetUnassignedListRequestPaginationParams => {
	const bypassFilters = Boolean(query) && !reviewSessionId;

	return {
		page,
		limit,
		searchValue: query,
		sorting,
		assignStatus: bypassFilters
			? READY_TO_SEND_ASSIGN_STATUSES
			: expandAssignStatus(selectedAssignStatus),
		filter: bypassFilters ? undefined : filter,
		inclusionFilterStatus: [InclusionFilterStatus.ADDED],
		priorityFilterStatus: bypassFilters ? [PriorityFilterStatus.ALL] : priorityFilterStatus,
		signalFilterStatus: bypassFilters ? undefined : (signalFilterStatus ?? undefined),
		listIds:
			!bypassFilters && selectedLists?.length > 0
				? [...(selectedLists?.map((list) => list.id as number) || [])]
				: undefined,
		...(reviewSessionId && { reviewSessionId }),
	};
};
